import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Target, TrendingUp, Send, Check, X, ArrowLeft, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function KonattuTargetsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [perf, setPerf] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entryDialog, setEntryDialog] = useState({ open: false, achieved: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [targetDialog, setTargetDialog] = useState({ open: false, employee_id: '', target: '' });
  const [konattuTargets, setKonattuTargets] = useState([]);

  const isSuperAdmin = user?.system_role === 'super_admin';
  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  const canSetTarget = ['super_admin', 'management'].includes(user?.system_role);
  const canSubmitDirect = !!user;

  const fetchData = useCallback(async () => {
    setEntries([]);
    try {
      const params = { month };
      if (selectedBranch) params.branch_id = selectedBranch;
      if (selectedDivision) params.division = selectedDivision;
      const [pRes, eRes, tRes] = await Promise.all([
        isManager ? api.get('/konattu-performance', { params }) : Promise.resolve({ data: null }),
        api.get('/konattu-entries', { params: { month } }),
        canSetTarget ? api.get('/konattu-targets', { params: { month } }) : Promise.resolve({ data: [] }),
      ]);
      setPerf(pRes.data);
      setEntries(eRes.data || []);
      setKonattuTargets(tRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [month, selectedDivision, selectedBranch, isManager, canSetTarget]);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 20000);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(i); window.removeEventListener('focus', onFocus); };
  }, [fetchData]);

  const handleSubmitDirect = async () => {
    if (!entryDialog.achieved || !entryDialog.date) { toast.error('Fill amount and date'); return; }
    try {
      await api.post('/konattu-entries', {
        achieved: parseFloat(entryDialog.achieved),
        date: entryDialog.date,
        notes: entryDialog.notes,
      });
      toast.success('Direct Konattu entry submitted');
      setEntryDialog({ open: false, achieved: '', date: new Date().toISOString().slice(0, 10), notes: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/konattu-entries/${id}/approve`); toast.success('Approved'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  // Set / update a Konattu Achievement Target for an employee in the selected
  // month. Mirrors the brand-target flow but stored in `konattu_targets`.
  const handleSetTarget = async () => {
    if (!targetDialog.employee_id) { toast.error('Pick an employee'); return; }
    const t = parseFloat(targetDialog.target);
    if (!t || t <= 0) { toast.error('Enter a target amount'); return; }
    try {
      await api.post('/konattu-targets', {
        employee_id: targetDialog.employee_id,
        month,
        target: t,
      });
      toast.success('Konattu target saved');
      setTargetDialog({ open: false, employee_id: '', target: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDeleteTarget = async (tid, empName) => {
    if (!window.confirm(`Remove Konattu target for ${empName}?`)) return;
    try {
      await api.delete(`/konattu-targets/${tid}`);
      toast.success('Target removed');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDelete = async (e) => {
    const extra = e.source === 'brand_rollup' ? '\n\nThis will also delete the source BRAND achievement.' : '';
    if (!window.confirm(`Delete ${e.status} ${e.source === 'brand_rollup' ? '(brand-linked)' : 'direct'} Konattu entry for ${e.employee_name}?${extra}`)) return;
    try {
      await api.delete(`/konattu-entries/${e.id}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const activeDivision = selectedDivision ? perf?.divisions?.find(d => d.division === selectedDivision) : null;
  const activeBranch = selectedBranch && activeDivision ? activeDivision.branches.find(b => b.branch_id === selectedBranch) : null;

  const visibleEntries = entries.filter(e => {
    if (selectedDivision) {
      // Match division via the staff row
      const inDiv = activeDivision?.branches.some(b => b.staff.some(s => s.employee_id === e.employee_id));
      if (!inDiv) return false;
    }
    if (selectedBranch && e.branch_id !== selectedBranch) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="konattu-targets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {(selectedDivision || selectedBranch) && (
            <Button variant="ghost" size="sm" className="h-8 rounded-sm -ml-2" onClick={() => {
              if (selectedBranch) setSelectedBranch(null);
              else setSelectedDivision(null);
            }} data-testid="konattu-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          <div>
            <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">
              {activeBranch?.branch_name || activeDivision?.division || 'Konattu Achievements'}
            </h1>
            <p className="text-neutral-500 text-sm mt-0.5">
              {month}
              {selectedDivision && ` · Division: ${selectedDivision}`}
              {activeBranch && ` · ${activeBranch.branch_name}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="konattu-month-filter" />
          {canSetTarget && (
            <Button variant="outline" className="rounded-sm border-neutral-300"
              onClick={() => setTargetDialog({ open: true, employee_id: '', target: '' })}
              data-testid="set-konattu-target-btn">
              <Target className="h-4 w-4 mr-2" /> Set Konattu Target
            </Button>
          )}
          {canSubmitDirect && (
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setEntryDialog(p => ({ ...p, open: true }))} data-testid="submit-konattu-direct">
              <Send className="h-4 w-4 mr-2" /> Add Non-Brand Sale
            </Button>
          )}
        </div>
      </div>

      {/* Totals banner */}
      {isManager && perf && (
        <Card className="border border-purple-200 bg-purple-50/30 shadow-sm rounded-md" data-testid="konattu-total-banner">
          <CardContent className="p-5 flex flex-col sm:flex-row justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-sm flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Konattu · {month}</p>
                <p className="text-2xl font-heading font-black text-neutral-900">{(perf.total_achieved || 0).toLocaleString()} <span className="text-sm font-medium text-neutral-500">/ {(perf.total_target || 0).toLocaleString()}</span></p>
              </div>
            </div>
            <div className="flex items-center">
              <Badge className={`text-sm px-3 py-1 ${perf.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : perf.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                {perf.pct}% achieved
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-28 bg-neutral-100 rounded-md animate-pulse" />)}
        </div>
      ) : isManager && !selectedDivision ? (
        /* Division cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(perf?.divisions || []).map(d => (
            <Card key={d.division}
              className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md hover:-translate-y-[1px] cursor-pointer transition-all"
              onClick={() => setSelectedDivision(d.division)}
              data-testid={`konattu-division-${d.division}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">Division</p>
                    <h3 className="font-heading font-black text-neutral-900 text-lg">{d.division}</h3>
                  </div>
                  <Badge className={`${d.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : d.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{d.pct}%</Badge>
                </div>
                <div className="flex items-end justify-between pt-1">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">Target</p>
                    <p className="text-sm font-semibold">{(d.total_target || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">Achieved</p>
                    <p className="text-sm font-semibold text-emerald-700">{(d.total_achieved || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isManager && selectedDivision && !selectedBranch && activeDivision ? (
        /* Branch cards inside division */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeDivision.branches.map(b => (
            <Card key={b.branch_id}
              className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md hover:-translate-y-[1px] cursor-pointer transition-all"
              onClick={() => setSelectedBranch(b.branch_id)}
              data-testid={`konattu-branch-${b.branch_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-neutral-500" />
                    <h3 className="font-heading font-bold text-neutral-900 text-sm">{b.branch_name}</h3>
                  </div>
                  <Badge className={`${b.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : b.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{b.pct}%</Badge>
                </div>
                <div className="flex items-end justify-between pt-1">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">Target</p>
                    <p className="text-sm font-semibold">{(b.total_target || 0).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-neutral-400">Achieved</p>
                    <p className="text-sm font-semibold text-emerald-700">{(b.total_achieved || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isManager && activeBranch ? (
        /* Staff-wise inside branch */
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold text-neutral-800">Staff Performance</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs uppercase">Staff</TableHead>
                    <TableHead className="text-xs uppercase">Brand</TableHead>
                    <TableHead className="text-xs uppercase text-right">Target</TableHead>
                    <TableHead className="text-xs uppercase text-right">Brand Sales</TableHead>
                    <TableHead className="text-xs uppercase text-right">Direct</TableHead>
                    <TableHead className="text-xs uppercase text-right">Total</TableHead>
                    <TableHead className="text-xs uppercase text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeBranch.staff.map(s => (
                    <TableRow key={s.employee_id}>
                      <TableCell className="text-sm font-medium">{s.employee_name}</TableCell>
                      <TableCell className="text-sm"><Badge variant="outline" className="text-xs">{s.brand || '-'}</Badge></TableCell>
                      <TableCell className="text-sm text-right">{(s.target || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right text-amber-700">{(s.brand_achieved || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right text-blue-700">{(s.direct_achieved || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right font-bold text-emerald-700">{(s.achieved || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right"><Badge className={`${s.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : s.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.pct}%</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Entries list */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-neutral-800">Entries ({visibleEntries.length})</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="text-xs uppercase">Date</TableHead>
                  <TableHead className="text-xs uppercase">Staff</TableHead>
                  <TableHead className="text-xs uppercase">Source</TableHead>
                  <TableHead className="text-xs uppercase text-right">Achieved</TableHead>
                  <TableHead className="text-xs uppercase">Status</TableHead>
                  {isManager && <TableHead className="text-xs uppercase text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleEntries.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-neutral-500">No entries for {month}</TableCell></TableRow>
                ) : visibleEntries.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">{e.date}</TableCell>
                    <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                    <TableCell className="text-sm">
                      {e.source === 'brand_rollup' ? (
                        <Badge className="text-[10px] bg-amber-100 text-amber-700">Brand: {e.brand_name}</Badge>
                      ) : (
                        <Badge className="text-[10px] bg-blue-100 text-blue-700">Direct</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-right font-semibold">{(e.achieved || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${e.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : e.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{e.status}</Badge>
                    </TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {e.status === 'pending' && e.source === 'direct' && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleApprove(e.id)} data-testid={`approve-konattu-${e.id}`}>
                              <Check className="h-4 w-4 text-emerald-600" />
                            </Button>
                          )}
                          {e.source === 'brand_rollup' && e.status === 'pending' && (
                            <span className="text-[10px] text-neutral-400 italic pr-2">approve via Brand page</span>
                          )}
                          {isSuperAdmin && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(e)} data-testid={`delete-konattu-${e.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Konattu Targets list (admin/management) */}
      {canSetTarget && konattuTargets.length > 0 && (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-neutral-700">Konattu Targets · {month}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Employee</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Target ₹</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Achieved ₹</TableHead>
                    <TableHead className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">%</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {konattuTargets.map(t => (
                    <TableRow key={t.id} className="hover:bg-neutral-50">
                      <TableCell className="text-sm text-neutral-800 py-2">{t.employee_name}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums py-2">{(t.target ?? 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums py-2">{(t.achieved ?? 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-sm text-right tabular-nums py-2">
                        <Badge className={`text-[10px] ${t.pct >= 100 ? 'bg-emerald-100 text-emerald-700' : t.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{t.pct ?? 0}%</Badge>
                      </TableCell>
                      <TableCell className="text-right py-2">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => handleDeleteTarget(t.id, t.employee_name)}
                          title="Remove target"
                          data-testid={`konattu-target-delete-${t.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Set Konattu target dialog */}
      <Dialog open={targetDialog.open} onOpenChange={(o) => setTargetDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="konattu-target-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Set Konattu Achievement Target</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-2">
              Sets the explicit Konattu target for an employee for <strong>{month}</strong>. Overrides the auto-rolled-up sum from brand targets.
            </p>
            <div className="space-y-1">
              <Label className="text-sm">Employee *</Label>
              <Select value={targetDialog.employee_id}
                onValueChange={v => setTargetDialog(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="konattu-target-employee">
                  <SelectValue placeholder="Select employee..." />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(perf?.divisions?.flatMap(d => d.branches.flatMap(b => b.staff)) || [])
                    .map(s => (
                      <SelectItem key={s.employee_id} value={s.employee_id}>{s.employee_name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Monthly Target (₹) *</Label>
              <Input type="number" min="0" step="1" value={targetDialog.target}
                onChange={e => setTargetDialog(p => ({ ...p, target: e.target.value }))}
                placeholder="e.g., 50000" className="rounded-sm"
                data-testid="konattu-target-amount" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setTargetDialog({ open: false, employee_id: '', target: '' })}>Cancel</Button>
            <Button onClick={handleSetTarget} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="konattu-target-save">Save Target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct Konattu entry dialog */}
      <Dialog open={entryDialog.open} onOpenChange={(o) => setEntryDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="konattu-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Add Non-Brand Konattu Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-2">
              Use this for accessories / non-branded sales only. Branded sales go through <strong>Brand Achievements</strong>.
            </p>
            <div className="space-y-1">
              <Label className="text-sm">Date</Label>
              <Input type="date" value={entryDialog.date} onChange={e => setEntryDialog(p => ({ ...p, date: e.target.value }))} className="rounded-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Amount *</Label>
              <Input type="number" value={entryDialog.achieved} onChange={e => setEntryDialog(p => ({ ...p, achieved: e.target.value }))} placeholder="e.g., 5000" className="rounded-sm" data-testid="konattu-entry-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Notes</Label>
              <Textarea value={entryDialog.notes} onChange={e => setEntryDialog(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEntryDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={handleSubmitDirect} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="konattu-entry-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
